import { useState, useRef, useCallback } from 'react';
import _ from 'lodash';
import * as VideoExpress from '@vonage/video-express';
import CameraPublisher from '@vonage/video-express/dist/mp/camera-publisher';
import ScreenPublisher from '@vonage/video-express/dist/mp/screen-publisher';
import RoomWrapper from '@vonage/video-express/dist/mp/room';
import {
  AUDIO_LEVEL,
  CURRENT_LOG_LEVEL,
  MOVING_AVERAGE,
  MAX_VIDEO_PARTICIPANTS,
  pageIds,
} from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { RoomProperties } from '@vonage/video-express/dist/utils/types';
import { Message } from './useSignal';
import ParticipantWrapper from '@vonage/video-express/dist/mp/participant';
import { decodeMediaInChat } from 'utilities/functions';
import { SignalEvent } from '@vonage/video-express/dist/types/signaling';

interface RoomParticipant {
  id: string;
  name: string;
}

export interface RoomCredentials {
  apikey: string;
  sessionId: string;
  token: string;
}

export interface RoomVideoEffects {
  backgroundBlur: boolean;
  videoSourceId: string;
}

export interface RoomPublisherOptions {
  publishAudio: boolean;
  publishVideo: boolean;
  audioSource?: string | undefined;
  videoSource?: string | undefined;
  audioOutput?: string | undefined;
}

interface RoomHook {
  createCall: (
    credentials: RoomCredentials,
    roomContainer: string,
    userName: string,
    videoEffects: RoomVideoEffects,
    publisherOptions: RoomPublisherOptions,
  ) => Promise<void>;
  connected: boolean;
  camera: CameraPublisher | null;
  screen: ScreenPublisher | null;
  room: VideoExpress.Room | null;
  participants: RoomParticipant[];
  networkStatus: string | null;
  publisherIsSpeaking: boolean;
  cameraPublishing: boolean;
  localParticipant: RoomParticipant | null;
  removeLocalParticipant: ({
    participant,
  }: {
    participant: RoomParticipant;
  }) => void;
  listOfMessages: Message[];
  sendSignal: (data: string, type: string) => void;
  addPublisherCameraEvents: () => void;
  toggleBlur: (enable: boolean) => Promise<void>;
}

export const useRoom = (): RoomHook => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const roomRef = useRef<VideoExpress.Room | null>(null);
  const publisherOptionsRef = useRef<RoomPublisherOptions | null>(null);
  const [camera, setCamera] = useState<CameraPublisher | null>(null);
  const [screen, setScreen] = useState<ScreenPublisher | null>(null);
  const [localParticipant, setLocalParticipant] =
    useState<RoomParticipant | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [networkStatus, setNetworkStatus] = useState<string | null>(null);
  const [publisherIsSpeaking, setPublisherIsSpeaking] =
    useState<boolean>(false);
  const [cameraPublishing, setCameraPublishing] = useState<boolean>(false);

  const [listOfMessages, setListOfMessages] = useState<Message[]>([]);

  const sendSignal = useCallback(
    (data: string, type: string) => {
      if (roomRef.current) {
        roomRef.current
          .signal({ type: type, data: data })
          .catch((e) => console.error(e));
      }
    },
    [roomRef],
  );

  const addMessageToList = useCallback(
    (
      data: string,
      isSentByMe: boolean,
      from: ParticipantWrapper | null | undefined,
      date: string,
    ) => {
      const decodedData = decodeMediaInChat(data);
      if (decodedData) {
        return setListOfMessages((prev) => [
          ...prev,
          {
            data,
            isSentByMe,
            from,
            date,
            imageData: {
              mediaId: decodedData.mediaId,
              mediaUrl: decodedData.mediaUrl,
            },
          },
        ]);
      }
      setListOfMessages((prev) => [...prev, { data, isSentByMe, from, date }]);
    },
    [],
  );

  const signalListener = useCallback(
    ({ data, isSentByMe, from }: SignalEvent) => {
      const now = new Date();
      const date = [
        now.getHours().toString().padStart(2, '0'),
        now.getMinutes().toString().padStart(2, '0'),
        now.getSeconds().toString().padStart(2, '0'),
        now.getMilliseconds().toString().padStart(3, '0'),
      ].join(':');
      addMessageToList(data as string, isSentByMe, from, date);
    },
    [addMessageToList],
  );

  const addParticipants = useCallback(
    ({ participant }: { participant: RoomParticipant }) => {
      setParticipants((prev) => [...prev, participant]);
    },
    [],
  );

  const removeParticipants = useCallback(
    ({ participant }: { participant: RoomParticipant }) => {
      setParticipants((prev) =>
        prev.filter((prevparticipant) => prevparticipant.id !== participant.id),
      );
    },
    [],
  );

  const addLocalParticipant = useCallback(
    ({ room }: { room: VideoExpress.Room }) => {
      if (room) {
        setLocalParticipant({
          id: room?.participantId || '',
          name: room?.participantName || '',
        });
      }
    },
    [],
  );

  const removeLocalParticipant = useCallback(() => {
    setParticipants([]);
  }, []);

  const onAudioLevel = useCallback((audioLevel: number) => {
    let movingAvg = null;
    if (movingAvg === null || movingAvg <= audioLevel) {
      movingAvg = audioLevel;
    } else {
      movingAvg = MOVING_AVERAGE * movingAvg + AUDIO_LEVEL * audioLevel;
    }

    const currentLogLevel = Math.log(movingAvg) / Math.LN10 / 1.5 + 1;
    if (currentLogLevel > CURRENT_LOG_LEVEL) {
      setPublisherIsSpeaking(true);
    } else {
      setPublisherIsSpeaking(false);
    }
  }, []);

  const addPublisherCameraEvents = useCallback(() => {
    if (roomRef.current?.camera) {
      roomRef.current.camera.on(
        'audioLevelUpdated',
        _.throttle((event) => onAudioLevel(event), 500),
      );
    }
  }, [onAudioLevel]);

  const startRoomListeners = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.on('connected', () => {
        roomRef.current?.on('signal', signalListener);
        console.log('Room: connected', new Date().toISOString());
      });
      roomRef.current.on('disconnected', async (reason: string) => {
        setNetworkStatus('disconnected');
        roomRef.current?.off('signal', signalListener);
        console.log('Room: disconnected', reason, new Date().toISOString());

        if (
          reason === 'networkDisconnected' ||
          reason === 'cameraPublisherError'
        ) {
          window.location.reload();
        }
      });
      roomRef.current?.camera.on('created', () => {
        setCameraPublishing(true);
        console.log('camera publishing now', new Date().toISOString());
      });
      roomRef.current.on('activeSpeakerChanged', (participant) => {
        console.log('Active speaker changed', participant);
      });

      roomRef.current.on('reconnected', () => {
        setNetworkStatus('reconnected');
        console.log('Room: reconnected', new Date().toISOString());
      });
      roomRef.current.on('reconnecting', () => {
        setNetworkStatus('reconnecting');
        console.log('Room: reconnecting', new Date().toISOString());
      });
      roomRef.current.on('participantJoined', (participant) => {
        addParticipants({ participant } as { participant: never });
        console.log('Room: participant joined: ', participant);
      });
      roomRef.current.on('participantLeft', (participant) => {
        removeParticipants({ participant } as { participant: never });
      });
    }
  }, [addParticipants, removeParticipants, signalListener]);

  const toggleBlur = useCallback(async (enable: boolean): Promise<void> => {
    const camera = roomRef.current?.camera;
    if (!camera) {
      return;
    }
    // Applied on the live publisher: leaving and rejoining the room to swap
    // the video source freezes the stream for every other participant.
    if (enable) {
      await camera.setVideoFilter({
        type: 'backgroundBlur',
        blurStrength: 'high',
      });
      return;
    }
    await camera.clearVideoFilter();
  }, []);

  const createCall = useCallback(
    async (
      {
        apikey,
        sessionId,
        token,
      }: { apikey: string; sessionId: string; token: string },
      roomContainer: string,
      userName: string,
      videoEffects: { backgroundBlur: boolean; videoSourceId: string },
      publisherOptions: RoomPublisherOptions,
    ): Promise<void> => {
      if (!apikey || !sessionId || !token) {
        throw new Error(locale?.invalidCredentials);
      }

      roomRef.current = new VideoExpress.Room({
        apiKey: apikey,
        sessionId: sessionId,
        token: token,
        roomContainer: 'roomContainer',
        maxVideoParticipantsOnScreen: MAX_VIDEO_PARTICIPANTS,
        participantName: userName,
        managedLayoutOptions: {
          deviceLayoutMode: 'auto',
          layoutMode: 'active-speaker',
          screenPublisherContainer: 'screenSharingContainer',
        },
      } as RoomProperties);

      startRoomListeners();

      publisherOptionsRef.current = Object.assign({}, publisherOptions, {
        style: {
          buttonDisplayMode: 'off',
          nameDisplayMode: 'auto',
          audioLevelDisplayMode: 'off',
        },
        name: userName,
        showControls: true,
      });

      roomRef.current
        ?.join({ publisherProperties: publisherOptionsRef.current as never })
        .then(async () => {
          setConnected(true);
          setCamera(roomRef.current?.camera || null);
          setScreen(roomRef.current?.screen || null);
          addLocalParticipant({ room: roomRef.current as RoomWrapper });

          if (videoEffects.backgroundBlur) {
            await toggleBlur(true);
          }
        })
        .catch((e) => console.warn(e));
    },
    [
      locale?.invalidCredentials,
      toggleBlur,
      startRoomListeners,
      addLocalParticipant,
    ],
  );

  return {
    createCall,
    connected,
    camera,
    screen,
    listOfMessages,
    sendSignal,
    room: roomRef.current,
    participants,
    networkStatus,
    publisherIsSpeaking,
    cameraPublishing,
    localParticipant,
    removeLocalParticipant,
    addPublisherCameraEvents,
    toggleBlur,
  };
};
